import { motion } from "framer-motion";
import { Activity, HeartPulse, ShieldPlus, Trophy, Hand, Zap, Dumbbell, ArrowUpRight } from "lucide-react";

const SERVICES = [
  { name: "Chronic Pain Management", desc: "Long-term relief strategies for persistent musculoskeletal pain, built around your life.", icon: Activity, span: "lg:col-span-2", img: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=1400&q=80" },
  { name: "Pelvic Health Physiotherapy", desc: "Discreet assessment, education and rehabilitation of pelvic floor function.", icon: HeartPulse, span: "", img: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=1200&q=80" },
  { name: "Post-Operative Rehabilitation", desc: "Structured recovery: pain and swelling management, mobility and a safe return to activity.", icon: ShieldPlus, span: "", img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=1200&q=80" },
  { name: "Sports Rehabilitation & Performance", desc: "Injury rehab, return-to-sport and physical preparation — including HYROX athletes.", icon: Trophy, span: "lg:col-span-2", img: "https://images.pexels.com/photos/20860597/pexels-photo-20860597.jpeg?auto=compress&cs=tinysrgb&w=1400" },
  { name: "Manual Therapy", desc: "Hands-on joint mobilisation, soft-tissue techniques and stretching.", icon: Hand, span: "lg:col-span-2", img: "https://images.unsplash.com/photo-1519824145371-296894a0daa9?auto=format&fit=crop&w=1400&q=80" },
  { name: "Electrotherapy & Modalities", desc: "Interferential therapy and therapeutic applications of heat and cold.", icon: Zap, span: "lg:col-span-2", img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=1400&q=80" },
  { name: "Exercise & Functional Rehabilitation", desc: "Individually prescribed programmes: mobility, strength, balance and conditioning.", icon: Dumbbell, span: "lg:col-span-2", img: "https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?auto=format&fit=crop&w=1400&q=80" },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="bg-navy py-28 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16 lg:mb-20">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-electric mb-6">What We Treat</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[0.95]">
            Seven ways we get<br />you moving again.
          </h2>
        </div>
        <p className="max-w-sm text-white/60 text-sm lg:text-base leading-relaxed">
          Assessment, treatment and rehabilitation across a wide range of musculoskeletal and
          functional conditions — every plan tailored to you.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.a
              key={s.name}
              href="#book"
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 4) * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`service-card-${i}`}
              className={`group relative flex flex-col justify-between overflow-hidden rounded-lg border border-white/10 p-8 min-h-[300px] hover:border-electric transition-colors duration-300 ${s.span}`}
            >
              <img
                src={s.img}
                alt={s.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-35 transition-[opacity,transform] duration-700 group-hover:opacity-55 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-navy/20" />
              <div className="relative z-10 flex items-start justify-between">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-electric text-white shadow-[0_8px_24px_rgba(0,122,255,0.45)]">
                  <Icon size={20} />
                </span>
                <ArrowUpRight size={20} className="text-white/50 transition-[color,transform] duration-300 group-hover:text-electric group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
              <div className="relative z-10">
                <h3 className="font-display font-bold text-xl lg:text-2xl text-white tracking-tight mb-2">{s.name}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </motion.a>
          );
        })}
      </div>
    </div>
  </section>
);
